/* factory.js — upgraded (FULL FILE)
 * Keeps:
 * - Drag, corner resize handles, rotation handle
 * - Bounding box
 * - Print area restriction (rect bounds)
 * - Mobile pinch zoom + rotate
 * - Max design size 11x11 inches
 * - Save -> Cloudinary -> attach Shopify line item properties incl final inches
 * Adds/Fixes:
 * - Restores selected variant preview on builder page
 * - Add to cart works on builder page (no product form required)
 * - Quantity supported (reads ?qty= from builder URL for packs)
 * - Matrix mode still supported (?mode=matrix&lines=...)
 */

(() => {
  // =========================
  // ✅ CLOUDINARY CONFIG
  // =========================
  const CLOUDINARY_CLOUD_NAME = "dsujldwoh";
  const CLOUDINARY_UPLOAD_PRESET = "ThefactoryDMV";
  const CLOUDINARY_BASE_FOLDER = "pack_orders";

  // Hard constraint: design max size
  const MAX_INCH = 11;

  // Canvas size
  const CANVAS_W = 700;
  const CANVAS_H = 700;

  // Handle sizes (px)
  const HANDLE_SIZE = 12;
  const ROTATE_HANDLE_OFFSET = 28;

  // Slider behavior
  const SLIDER_MIN_SCALE = 0.15;
  const SLIDER_MAX_SCALE = 2.0;

  // =========================
  // DOM
  // =========================
  const app = document.querySelector(".factoryApp");
  if (!app) return;

  const canvas = document.getElementById("factoryCanvas");
  const ctx = canvas.getContext("2d", { willReadFrequently: true });

  const imageUpload = document.getElementById("imageUpload");
  const sizeSlider = document.getElementById("sizeSlider");
  const sizeValue = document.getElementById("sizeValue");
  const resetButton = document.getElementById("resetButton");
  const downloadButton = document.getElementById("downloadButton");
  const finalizeBtn = document.getElementById("finalizeBtn");
  const fileInfo = document.getElementById("fileInfo");

  const attrWidth = document.getElementById("attrWidth");
  const attrHeight = document.getElementById("attrHeight");
  const attrRotate = document.getElementById("attrRotate");
  const attrScale = document.getElementById("attrScale");

  // Variant preview elements
  const ui = {
    builderProductTitle: document.getElementById("builderProductTitle"),
    builderVariantTitle: document.getElementById("builderVariantTitle"),
    builderVariantPrice: document.getElementById("builderVariantPrice"),
    builderQtyLine: document.getElementById("builderQtyLine"),
    stickyTitle: document.getElementById("stickyTitle"),
    stickySub: document.getElementById("stickySub"),
    stickyPrice: document.getElementById("stickyPrice"),
    cartStatus: document.getElementById("cartStatus"),
    addBtn: document.getElementById("builderAddToCartBtn")
  };

  // Data attributes
  const mockupUrl = app.dataset.mockup || app.dataset.assetBase;
  const maskUrl = app.dataset.mask;

  const productHandle = app.dataset.productHandle || "custom_product";
  const cloudFolder = `${CLOUDINARY_BASE_FOLDER}/${productHandle}`;

  // Print area bounds in canvas coords
  const printBounds = {
    x: Number(app.dataset.printX) || 175,
    y: Number(app.dataset.printY) || 160,
    w: Number(app.dataset.printW) || 350,
    h: Number(app.dataset.printH) || 420
  };

  // Print area inches mapping
  const PRINT_AREA_IN = {
    w: Number(app.dataset.printWIn) || Number(app.getAttribute("data-print-w-in")) || 12,
    h: Number(app.dataset.printHIn) || Number(app.getAttribute("data-print-h-in")) || 16
  };

  // =========================
  // State
  // =========================
  const state = {
    mockupImg: null,
    maskImg: null,
    designImg: null,

    cx: CANVAS_W / 2,
    cy: CANVAS_H / 2,
    rotation: 0,
    baseScale: 1,
    sliderVal: 100,

    mode: "idle",
    activeHandle: null,

    dragStart: null,
    rotateStart: null,
    resizeStart: null,

    pinch: {
      active: false,
      startDist: 0,
      startAngle: 0,
      startScale: 1,
      startRot: 0,
      startCenter: null
    },

    designUrl: "",
    designMeta: ""
  };

  // =========================
  // Utilities
  // =========================
  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
  const deg = (rad) => rad * (180 / Math.PI);
  const fmt = (n, d = 2) => (isFinite(n) ? Number(n).toFixed(d) : "");

  function loadImage(url) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = url;
    });
  }

  function setStatus(text) {
    if (fileInfo) fileInfo.textContent = text || "";
    if (ui.cartStatus) ui.cartStatus.textContent = text || "";
  }

  function getPointerPos(e) {
    const rect = canvas.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    const x = ((p.clientX - rect.left) / rect.width) * CANVAS_W;
    const y = ((p.clientY - rect.top) / rect.height) * CANVAS_H;
    return { x, y };
  }

  function getTwoTouchPos(e) {
    const rect = canvas.getBoundingClientRect();
    const t0 = e.touches[0];
    const t1 = e.touches[1];
    const p0 = {
      x: ((t0.clientX - rect.left) / rect.width) * CANVAS_W,
      y: ((t0.clientY - rect.top) / rect.height) * CANVAS_H
    };
    const p1 = {
      x: ((t1.clientX - rect.left) / rect.width) * CANVAS_W,
      y: ((t1.clientY - rect.top) / rect.height) * CANVAS_H
    };
    return { p0, p1 };
  }

  const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
  const angle = (a, b) => Math.atan2(b.y - a.y, b.x - a.x);

  function rotatePoint(px, py, cx, cy, r) {
    const s = Math.sin(r), c = Math.cos(r);
    const dx = px - cx, dy = py - cy;
    return { x: cx + dx * c - dy * s, y: cy + dx * s + dy * c };
  }
  const inverseRotatePoint = (px, py, cx, cy, r) => rotatePoint(px, py, cx, cy, -r);

  // Convert design pixels -> inches using printBounds mapping
  const pxToInX = (px) => (px / printBounds.w) * PRINT_AREA_IN.w;
  const pxToInY = (px) => (px / printBounds.h) * PRINT_AREA_IN.h;

  // =========================
  // Line item properties storage (inputs created even if no form)
  // =========================
  function ensureLineItemInputs() {
    // We store these inputs on the builder container itself so we always have them.
    let urlInput = document.getElementById("designUrlInput");
    let metaInput = document.getElementById("designMetaInput");
    let sizeInput = document.getElementById("designSizeInput");

    if (!urlInput) {
      urlInput = document.createElement("input");
      urlInput.type = "hidden";
      urlInput.id = "designUrlInput";
      urlInput.name = "properties[Design URL]";
      app.appendChild(urlInput);
    }
    if (!metaInput) {
      metaInput = document.createElement("input");
      metaInput.type = "hidden";
      metaInput.id = "designMetaInput";
      metaInput.name = "properties[Design Meta]";
      app.appendChild(metaInput);
    }
    if (!sizeInput) {
      sizeInput = document.createElement("input");
      sizeInput.type = "hidden";
      sizeInput.id = "designSizeInput";
      sizeInput.name = "properties[Print Size]";
      app.appendChild(sizeInput);
    }

    return { urlInput, metaInput, sizeInput };
  }

  function writeProperties(url, metaObj) {
    const pack = ensureLineItemInputs();
    pack.urlInput.value = url || "";
    pack.metaInput.value = metaObj ? JSON.stringify(metaObj) : "";

    if (metaObj?.final_print?.width_in && metaObj?.final_print?.height_in) {
      pack.sizeInput.value = `${fmt(metaObj.final_print.width_in, 2)}in x ${fmt(metaObj.final_print.height_in, 2)}in`;
    } else {
      pack.sizeInput.value = "";
    }
  }

  // =======================
  // Cart modes (single vs matrix)
  // =======================
  function getModeFromURL() {
    const params = new URLSearchParams(window.location.search);
    return params.get("mode") || "single";
  }

  function getLinesFromURL() {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("lines");
    if (!raw) return [];
    try {
      const decoded = decodeURIComponent(raw);
      const lines = JSON.parse(decoded);
      return Array.isArray(lines) ? lines : [];
    } catch {
      return [];
    }
  }

  function getVariantIdFromURL() {
    const params = new URLSearchParams(window.location.search);
    const v = params.get("variant");
    return v ? Number(v) : null;
  }

  function getQtyFromURL() {
    const params = new URLSearchParams(window.location.search);
    const q = parseInt(params.get("qty") || params.get("quantity") || "1", 10);
    return Number.isFinite(q) && q > 0 ? q : 1;
  }

  function fmtMoney(cents) {
    try {
      const currency =
        (window.Shopify && Shopify.currency && Shopify.currency.active)
          ? Shopify.currency.active
          : "USD";
      return new Intl.NumberFormat(undefined, { style: "currency", currency }).format((cents || 0) / 100);
    } catch {
      return `$${((cents || 0) / 100).toFixed(2)}`;
    }
  }

  async function fetchVariantJSON(variantId) {
    const res = await fetch(`/variants/${variantId}.js`, { headers: { Accept: "application/json" } });
    if (!res.ok) throw new Error("Could not load variant details.");
    return res.json();
  }

  async function hydrateBuilderPreview() {
    const variantId = getVariantIdFromURL();
    const qty = getQtyFromURL();

    if (ui.builderQtyLine) ui.builderQtyLine.textContent = `Quantity: ${qty}`;

    if (!variantId) {
      if (ui.builderProductTitle) ui.builderProductTitle.textContent = "No variant selected";
      if (ui.builderVariantTitle) ui.builderVariantTitle.textContent = "Go back and choose size/color first.";
      if (ui.builderVariantPrice) ui.builderVariantPrice.textContent = "";
      if (ui.stickyTitle) ui.stickyTitle.textContent = "No variant selected";
      if (ui.stickySub) ui.stickySub.textContent = "—";
      if (ui.stickyPrice) ui.stickyPrice.textContent = "—";
      return;
    }

    try {
      const v = await fetchVariantJSON(variantId);
      const name = v.name || v.title || "Selected item";
      const options = [v.option1, v.option2, v.option3].filter(Boolean).join(" / ");
      const price = fmtMoney(v.price);

      if (ui.builderProductTitle) ui.builderProductTitle.textContent = name;
      if (ui.builderVariantTitle) ui.builderVariantTitle.textContent = options || "";
      if (ui.builderVariantPrice) ui.builderVariantPrice.textContent = price;

      if (ui.stickyTitle) ui.stickyTitle.textContent = name;
      if (ui.stickySub) ui.stickySub.textContent = options ? `${options} • Qty ${qty}` : `Qty ${qty}`;
      if (ui.stickyPrice) ui.stickyPrice.textContent = price;
    } catch (e) {
      console.error(e);
      if (ui.builderProductTitle) ui.builderProductTitle.textContent = "Could not load variant info";
      if (ui.builderVariantTitle) ui.builderVariantTitle.textContent = "";
      if (ui.builderVariantPrice) ui.builderVariantPrice.textContent = "";
    }
  }

  async function addMatrixToCart(designUrl, printSizeText, designMetaJson) {
    const lines = getLinesFromURL();
    if (!lines.length) throw new Error("No quantities selected.");

    const properties = {
      "Design URL": designUrl,
      "Print Size": printSizeText || "",
      "Design Meta": designMetaJson || ""
    };

    const payload = {
      items: lines.map(l => ({
        id: Number(l.id),
        quantity: Number(l.quantity),
        properties
      }))
    };

    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(payload)
    });

    if (!res.ok) {
      const txt = await res.text();
      throw new Error(txt || "Add to cart failed.");
    }
    return res.json();
  }

  async function addSingleToCart(variantId, qty, properties) {
    const payload = {
      items: [
        { id: Number(variantId), quantity: Number(qty), properties: properties || {} }
      ]
    };

    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(payload)
    });

    if (!res.ok) {
      const txt = await res.text();
      throw new Error(txt || "Add to cart failed.");
    }
    return res.json();
  }

  // =========================
  // Drawing & geometry
  // =========================
  const off = document.createElement("canvas");
  off.width = CANVAS_W;
  off.height = CANVAS_H;
  const offCtx = off.getContext("2d");

  const masked = document.createElement("canvas");
  masked.width = CANVAS_W;
  masked.height = CANVAS_H;
  const maskedCtx = masked.getContext("2d");

  function sliderScale() {
    const v = Number(sizeSlider?.value ?? 100);
    state.sliderVal = v;
    const t = v / 100;
    return SLIDER_MIN_SCALE + t * (SLIDER_MAX_SCALE - SLIDER_MIN_SCALE);
  }

  function baseFitSize() {
    if (!state.designImg) return { w: 0, h: 0 };
    const maxW = printBounds.w * 0.55;
    const maxH = printBounds.h * 0.55;

    const ratio = state.designImg.width / state.designImg.height;
    let w = maxW, h = maxW / ratio;
    if (h > maxH) { h = maxH; w = maxH * ratio; }
    return { w, h };
  }

  function currentDesignSizePx() {
    if (!state.designImg) return { w: 0, h: 0 };
    const base = baseFitSize();
    const s = state.baseScale * sliderScale();
    return { w: base.w * s, h: base.h * s };
  }

  function getOBB() {
    const { w, h } = currentDesignSizePx();
    const cx = state.cx, cy = state.cy, r = state.rotation;
    const halfW = w / 2, halfH = h / 2;

    const tl = rotatePoint(cx - halfW, cy - halfH, cx, cy, r);
    const tr = rotatePoint(cx + halfW, cy - halfH, cx, cy, r);
    const br = rotatePoint(cx + halfW, cy + halfH, cx, cy, r);
    const bl = rotatePoint(cx - halfW, cy + halfH, cx, cy, r);

    const topMid = { x: (tl.x + tr.x) / 2, y: (tl.y + tr.y) / 2 };
    const rotatePt = rotatePoint(topMid.x, topMid.y - ROTATE_HANDLE_OFFSET, topMid.x, topMid.y, r);

    return { tl, tr, br, bl, topMid, rotatePt, w, h };
  }

  function pointInRotRect(px, py) {
    const local = inverseRotatePoint(px, py, state.cx, state.cy, state.rotation);
    const { w, h } = currentDesignSizePx();
    return (
      local.x >= state.cx - w / 2 &&
      local.x <= state.cx + w / 2 &&
      local.y >= state.cy - h / 2 &&
      local.y <= state.cy + h / 2
    );
  }

  function hitHandle(px, py) {
    const obb = getOBB();
    const handles = [
      { name: "tl", p: obb.tl },
      { name: "tr", p: obb.tr },
      { name: "br", p: obb.br },
      { name: "bl", p: obb.bl }
    ];

    for (const h of handles) {
      if (Math.abs(px - h.p.x) <= HANDLE_SIZE && Math.abs(py - h.p.y) <= HANDLE_SIZE) return h.name;
    }

    if (Math.abs(px - obb.rotatePt.x) <= HANDLE_SIZE && Math.abs(py - obb.rotatePt.y) <= HANDLE_SIZE) return "rotate";
    return null;
  }

  function clampInsidePrintBounds() {
    const obb = getOBB();
    const corners = [obb.tl, obb.tr, obb.br, obb.bl];

    let dx = 0, dy = 0;
    const minX = Math.min(...corners.map(p => p.x));
    const maxX = Math.max(...corners.map(p => p.x));
    const minY = Math.min(...corners.map(p => p.y));
    const maxY = Math.max(...corners.map(p => p.y));

    const left = printBounds.x;
    const right = printBounds.x + printBounds.w;
    const top = printBounds.y;
    const bottom = printBounds.y + printBounds.h;

    if (minX < left) dx = left - minX;
    if (maxX > right) dx = right - maxX;
    if (minY < top) dy = top - minY;
    if (maxY > bottom) dy = bottom - maxY;

    state.cx += dx;
    state.cy += dy;
  }

  function enforceMax11Inches() {
    const { w, h } = currentDesignSizePx();
    const wIn = pxToInX(w);
    const hIn = pxToInY(h);

    let factor = 1;
    if (wIn > MAX_INCH) factor = Math.min(factor, MAX_INCH / wIn);
    if (hIn > MAX_INCH) factor = Math.min(factor, MAX_INCH / hIn);
    if (factor < 1) state.baseScale *= factor;
  }

  function updateAttributes() {
    if (!state.designImg) {
      attrWidth.value = "";
      attrHeight.value = "";
      attrRotate.value = "";
      attrScale.value = "";
      return;
    }
    const { w, h } = currentDesignSizePx();
    attrWidth.value = fmt(pxToInX(w), 2);
    attrHeight.value = fmt(pxToInY(h), 2);
    attrRotate.value = fmt(deg(state.rotation), 1);
    attrScale.value = fmt((state.baseScale * sliderScale()) * 100, 0);
  }

  function drawOverlayControls() {
    if (!state.designImg) return;
    const obb = getOBB();

    ctx.save();
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 4]);

    ctx.beginPath();
    ctx.moveTo(obb.tl.x, obb.tl.y);
    ctx.lineTo(obb.tr.x, obb.tr.y);
    ctx.lineTo(obb.br.x, obb.br.y);
    ctx.lineTo(obb.bl.x, obb.bl.y);
    ctx.closePath();
    ctx.strokeStyle = "rgba(255,255,255,0.9)";
    ctx.stroke();

    ctx.setLineDash([]);
    const corners = [obb.tl, obb.tr, obb.br, obb.bl];
    for (const p of corners) {
      ctx.fillStyle = "rgba(255,255,255,0.95)";
      ctx.fillRect(p.x - HANDLE_SIZE / 2, p.y - HANDLE_SIZE / 2, HANDLE_SIZE, HANDLE_SIZE);
      ctx.strokeStyle = "rgba(0,0,0,0.25)";
      ctx.strokeRect(p.x - HANDLE_SIZE / 2, p.y - HANDLE_SIZE / 2, HANDLE_SIZE, HANDLE_SIZE);
    }

    ctx.beginPath();
    ctx.moveTo(obb.topMid.x, obb.topMid.y);
    ctx.lineTo(obb.rotatePt.x, obb.rotatePt.y);
    ctx.strokeStyle = "rgba(255,255,255,0.8)";
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(obb.rotatePt.x, obb.rotatePt.y, HANDLE_SIZE / 2, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(255,255,255,0.95)";
    ctx.fill();
    ctx.strokeStyle = "rgba(0,0,0,0.25)";
    ctx.stroke();

    ctx.restore();
  }

  function render() {
    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
    if (state.mockupImg) ctx.drawImage(state.mockupImg, 0, 0, CANVAS_W, CANVAS_H);

    if (!state.designImg) {
      updateAttributes();
      return;
    }

    offCtx.clearRect(0, 0, CANVAS_W, CANVAS_H);
    const { w, h } = currentDesignSizePx();

    offCtx.save();
    offCtx.translate(state.cx, state.cy);
    offCtx.rotate(state.rotation);
    offCtx.drawImage(state.designImg, -w / 2, -h / 2, w, h);
    offCtx.restore();

    maskedCtx.clearRect(0, 0, CANVAS_W, CANVAS_H);
    maskedCtx.drawImage(off, 0, 0);

    if (state.maskImg) {
      maskedCtx.globalCompositeOperation = "destination-in";
      maskedCtx.drawImage(state.maskImg, 0, 0, CANVAS_W, CANVAS_H);
      maskedCtx.globalCompositeOperation = "source-over";
    }

    ctx.drawImage(masked, 0, 0);
    drawOverlayControls();
    updateAttributes();
  }

  function setCursorForHit(px, py) {
    if (!state.designImg) { canvas.style.cursor = "default"; return; }
    const h = hitHandle(px, py);
    if (h === "rotate") { canvas.style.cursor = "grab"; return; }
    if (h === "tl" || h === "br") { canvas.style.cursor = "nwse-resize"; return; }
    if (h === "tr" || h === "bl") { canvas.style.cursor = "nesw-resize"; return; }
    if (pointInRotRect(px, py)) { canvas.style.cursor = "move"; return; }
    canvas.style.cursor = "default";
  }

  // =========================
  // Cloudinary
  // =========================
  async function uploadToCloudinary(blob) {
    const form = new FormData();
    form.append("file", blob);
    form.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);
    form.append("folder", cloudFolder);

    const endpoint = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`;
    const res = await fetch(endpoint, { method: "POST", body: form });
    const data = await res.json();

    if (!res.ok) throw new Error(data?.error?.message || "Cloudinary upload failed.");
    return data;
  }

  function canvasToBlob() {
    return new Promise((resolve) => canvas.toBlob(resolve, "image/png", 1));
  }

  // =========================
  // Interaction
  // =========================
  function onPointerDown(e) {
    if (!state.designImg) return;
    const { x, y } = getPointerPos(e);

    if (e.touches && e.touches.length === 2) {
      const { p0, p1 } = getTwoTouchPos(e);
      state.mode = "pinch";
      state.pinch.active = true;
      state.pinch.startDist = dist(p0, p1);
      state.pinch.startAngle = angle(p0, p1);
      state.pinch.startScale = state.baseScale;
      state.pinch.startRot = state.rotation;
      state.pinch.startCenter = { x: (p0.x + p1.x) / 2, y: (p0.y + p1.y) / 2 };
      e.preventDefault();
      return;
    }

    const h = hitHandle(x, y);
    if (h === "rotate") {
      state.mode = "rotate";
      state.rotateStart = { x, y, rot: state.rotation };
      e.preventDefault();
      return;
    }

    if (h === "tl" || h === "tr" || h === "br" || h === "bl") {
      state.mode = "resize";
      state.activeHandle = h;
      const obb = getOBB();
      state.resizeStart = {
        x, y,
        startW: obb.w,
        startH: obb.h,
        startScale: state.baseScale
      };
      e.preventDefault();
      return;
    }

    if (pointInRotRect(x, y)) {
      state.mode = "drag";
      state.dragStart = { x, y, cx: state.cx, cy: state.cy };
      e.preventDefault();
    }
  }

  function onPointerMove(e) {
    const { x, y } = getPointerPos(e);

    if (!state.designImg) { canvas.style.cursor = "default"; return; }
    if (!e.touches) setCursorForHit(x, y);

    if (e.touches && e.touches.length === 2 && state.mode === "pinch" && state.pinch.active) {
      const { p0, p1 } = getTwoTouchPos(e);
      const d = dist(p0, p1);
      const a = angle(p0, p1);

      const scaleFactor = d / state.pinch.startDist;
      state.baseScale = clamp(state.pinch.startScale * scaleFactor, 0.05, 10);

      const rotDelta = a - state.pinch.startAngle;
      state.rotation = state.pinch.startRot + rotDelta;

      const mid = { x: (p0.x + p1.x) / 2, y: (p0.y + p1.y) / 2 };
      const startMid = state.pinch.startCenter;
      state.cx += (mid.x - startMid.x);
      state.cy += (mid.y - startMid.y);
      state.pinch.startCenter = mid;

      enforceMax11Inches();
      clampInsidePrintBounds();
      render();
      e.preventDefault();
      return;
    }

    if (state.mode === "drag" && state.dragStart) {
      const dx = x - state.dragStart.x;
      const dy = y - state.dragStart.y;
      state.cx = state.dragStart.cx + dx;
      state.cy = state.dragStart.cy + dy;

      clampInsidePrintBounds();
      render();
      e.preventDefault();
      return;
    }

    if (state.mode === "rotate" && state.rotateStart) {
      const a0 = Math.atan2(state.rotateStart.y - state.cy, state.rotateStart.x - state.cx);
      const a1 = Math.atan2(y - state.cy, x - state.cx);
      const delta = a1 - a0;
      state.rotation = state.rotateStart.rot + delta;

      clampInsidePrintBounds();
      render();
      e.preventDefault();
      return;
    }

    if (state.mode === "resize" && state.resizeStart) {
      const localStart = inverseRotatePoint(state.resizeStart.x, state.resizeStart.y, state.cx, state.cy, state.rotation);
      const localNow = inverseRotatePoint(x, y, state.cx, state.cy, state.rotation);

      const dx = localNow.x - localStart.x;
      const dy = localNow.y - localStart.y;

      const aspect = state.resizeStart.startW / state.resizeStart.startH;

      let newW = state.resizeStart.startW;
      if (state.activeHandle === "tr" || state.activeHandle === "br") newW = state.resizeStart.startW + dx * 2;
      if (state.activeHandle === "tl" || state.activeHandle === "bl") newW = state.resizeStart.startW - dx * 2;

      let newH = state.resizeStart.startH;
      if (state.activeHandle === "bl" || state.activeHandle === "br") newH = state.resizeStart.startH + dy * 2;
      if (state.activeHandle === "tl" || state.activeHandle === "tr") newH = state.resizeStart.startH - dy * 2;

      if (Math.abs(newW - state.resizeStart.startW) > Math.abs(newH - state.resizeStart.startH)) {
        newH = newW / aspect;
      } else {
        newW = newH * aspect;
      }

      const base = baseFitSize();
      const desiredScale = (newW / base.w) / sliderScale();
      state.baseScale = clamp(desiredScale, 0.05, 10);

      enforceMax11Inches();
      clampInsidePrintBounds();
      render();
      e.preventDefault();
      return;
    }
  }

  function onPointerUp() {
    if (state.mode === "pinch") state.pinch.active = false;
    state.mode = "idle";
    state.activeHandle = null;
    state.dragStart = null;
    state.rotateStart = null;
    state.resizeStart = null;
  }

  // =========================
  // Controls
  // =========================
  imageUpload?.addEventListener("change", async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setStatus(`Loaded: ${file.name}`);

    const url = URL.createObjectURL(file);
    try {
      state.designImg = await loadImage(url);

      state.cx = printBounds.x + printBounds.w / 2;
      state.cy = printBounds.y + printBounds.h / 2;
      state.rotation = 0;
      state.baseScale = 1;

      if (sizeSlider) sizeSlider.value = "60";
      if (sizeValue) sizeValue.textContent = "60%";

      enforceMax11Inches();
      clampInsidePrintBounds();

      state.designUrl = "";
      state.designMeta = "";
      writeProperties("", null);

      render();
    } catch (err) {
      console.error(err);
      setStatus("Could not load that image.");
    }
  });

  sizeSlider?.addEventListener("input", () => {
    if (sizeValue) sizeValue.textContent = `${sizeSlider.value}%`;
    enforceMax11Inches();
    clampInsidePrintBounds();
    render();
  });

  resetButton?.addEventListener("click", () => {
    state.cx = printBounds.x + printBounds.w / 2;
    state.cy = printBounds.y + printBounds.h / 2;
    state.rotation = 0;
    state.baseScale = 1;

    if (sizeSlider) sizeSlider.value = "60";
    if (sizeValue) sizeValue.textContent = "60%";

    state.designUrl = "";
    state.designMeta = "";
    writeProperties("", null);

    if (state.designImg) {
      enforceMax11Inches();
      clampInsidePrintBounds();
    }

    render();
    setStatus("Reset.");
  });

  downloadButton?.addEventListener("click", () => {
    const link = document.createElement("a");
    link.download = "mockup.png";
    link.href = canvas.toDataURL("image/png");
    link.click();
  });

  // Save -> Cloudinary -> attach properties with FINAL inches
  finalizeBtn?.addEventListener("click", async () => {
    if (!state.designImg) {
      setStatus("Upload a design first.");
      return;
    }

    try {
      setStatus("Saving design…");

      enforceMax11Inches();
      clampInsidePrintBounds();
      render();

      const blob = await canvasToBlob();
      if (!blob) throw new Error("Could not export PNG.");

      const uploaded = await uploadToCloudinary(blob);

      const { w, h } = currentDesignSizePx();
      const finalWIn = pxToInX(w);
      const finalHIn = pxToInY(h);

      const meta = {
        design_url: uploaded.secure_url,
        public_id: uploaded.public_id,
        created_at: uploaded.created_at,
        product_handle: productHandle,
        constraints: { max_in: MAX_INCH },
        final_print: {
          width_in: Number(fmt(finalWIn, 2)),
          height_in: Number(fmt(finalHIn, 2)),
          rotation_deg: Number(fmt(deg(state.rotation), 1))
        },
        placement: {
          center_x: Number(fmt(state.cx, 1)),
          center_y: Number(fmt(state.cy, 1)),
          base_scale: Number(fmt(state.baseScale, 4)),
          slider: Number(sizeSlider?.value ?? 60)
        },
        print_bounds: printBounds,
        print_area_in: PRINT_AREA_IN
      };

      state.designUrl = uploaded.secure_url;
      state.designMeta = JSON.stringify(meta);

      writeProperties(state.designUrl, meta);
      setStatus("Saved! Now click Add to cart.");
    } catch (err) {
      console.error(err);
      setStatus(`Save failed: ${err.message || err}`);
    }
  });

  // =========================
  // Init
  // =========================
  async function init() {
    canvas.width = CANVAS_W;
    canvas.height = CANVAS_H;

    try {
      if (mockupUrl) state.mockupImg = await loadImage(mockupUrl);
      if (maskUrl) state.maskImg = await loadImage(maskUrl);
    } catch (e) {
      console.error(e);
      setStatus("Could not load mockup/mask.");
    }

    ensureLineItemInputs();
    await hydrateBuilderPreview();

    // Add to cart
    if (ui.addBtn) {
      ui.addBtn.addEventListener("click", async () => {
        const mode = getModeFromURL();

        // Require save first
        const pack = ensureLineItemInputs();
        const designUrl = pack.urlInput.value;
        if (!designUrl) {
          setStatus('Please click "Save Design" first.');
          return;
        }

        const printSize = pack.sizeInput.value || "";
        const meta = pack.metaInput.value || "";

        try {
          setStatus("Adding to cart…");

          if (mode === "matrix") {
            await addMatrixToCart(designUrl, printSize, meta);
            setStatus("Added to cart!");
            window.location.href = "/cart";
            return;
          }

          const variantId = getVariantIdFromURL();
          const qty = getQtyFromURL();
          if (!variantId) throw new Error("Missing variant in URL.");

          const properties = {
            "Design URL": designUrl,
            "Print Size": printSize,
            "Design Meta": meta
          };

          await addSingleToCart(variantId, qty, properties);

          setStatus("Added to cart!");
          window.location.href = "/cart";
        } catch (e) {
          console.error(e);
          setStatus(`Error: ${e.message || e}`);
        }
      });
    }

    // Mouse
    canvas.addEventListener("mousedown", onPointerDown);
    window.addEventListener("mousemove", onPointerMove);
    window.addEventListener("mouseup", onPointerUp);

    // Touch
    canvas.addEventListener("touchstart", (e) => { onPointerDown(e); }, { passive: false });
    window.addEventListener("touchmove", (e) => { onPointerMove(e); }, { passive: false });
    window.addEventListener("touchend", () => { onPointerUp(); }, { passive: false });

    // Cursor hover
    canvas.addEventListener("mousemove", (e) => {
      const { x, y } = getPointerPos(e);
      setCursorForHit(x, y);
    });

    if (sizeSlider && sizeValue) sizeValue.textContent = `${sizeSlider.value}%`;

    render();
  }

  init();
})();